import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import {
  withRouter,
  RouteComponentProps,
  generatePath,
} from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

import AddIcon from "@material-ui/icons/Add";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";

import { Button, Typography } from "@material-ui/core";

import ActiveProgramDictionary from "stores/dictionary/ActiveProgramDictionary";
import CategoryDetailsStore from "stores/CategoryDetailsStore";
import InAppStudioStore from "stores/InAppStudioStore";
import WorkoutTypeDictionary from "stores/dictionary/WorkoutTypeDictionary";

import Container from "components/Layout/Container";
import Header from "components/Layout/Header";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import Page from "components/Layout/Page";
import ConfirmationDialog from "components/ConfirmationDialog";
import Placeholder from "components/Placeholder";
import PublishStatus from "components/PublishStatus";
import FilteringControl from "components/FilteringControl";
import PublishButton from "components/PublishButton";
import { SNACKBAR_TYPES } from "components/Snackbar";

import AccessForm from "./AccessForm";
import CategoryDetailsCard from "./CategoryDetailsCard";
import WorkoutForm from "./WorkoutForm";

import { getDayOfWeek, getIntervalText, getMonthWithDay } from "utils/date";
import { areStatusActiveProgramFiltersEmpty } from "utils/filter";
import { getConfirmRemovalText } from "utils/removeItem";

import { PROGRAM_PERMISSION_TYPES, PUBLISH_STATUSES } from "constants/models";
import { FILTER_COLUMNS } from "constants/filterConstants";

import ROUTES from "navigation/routes";

import {
  CategoryCardWrapper,
  BackButtonWrapper,
  SearchContainer,
  Search,
  SearchControls,
  SearchControlWrapper,
  ScrollLoader,
  WeekCategoryPageSubtitle,
  WeekCategoryContainer,
  WeekCategoryHeader,
  Wrapper,
  OverflowLoader,
  Divider,
  PublishStatusWrapper,
} from "./styles";

type Props = GlobalPopupProps &
  SnackbarContextProps &
  RouteComponentProps<{ id: string }> & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class CategoryDetails extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;

  get categoryId() {
    const { id } = this.props.match.params;

    return id ? Number.parseInt(id) : null;
  }

  @computed get category() {
    return InAppStudioStore.categoryDetails;
  }

  @computed
  get isWeekCategory() {
    return this.category?.isWeekCategory;
  }

  @computed get weekCategoryDays() {
    return this.category?.weekCategoryDays;
  }

  @computed get weekCategoryWorkouts() {
    return this.weekCategoryDays?.map((day: Date, index: number) => ({
      day,
      dayNumber: index + 1,
      workouts: this.workouts.filter(
        ({ day }: { day: number }) => day === index + 1,
      ),
    }));
  }

  @computed get categoryTitle() {
    return this.category?.title;
  }

  @computed get categoryPublishStatus() {
    return this.category?.publishStatus;
  }

  @computed get shouldShowUpdateConfirmation() {
    return (
      this.categoryPublishStatus &&
      [
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
        PUBLISH_STATUSES.PUBLISHED,
      ].includes(this.categoryPublishStatus)
    );
  }

  @computed get workouts() {
    return CategoryDetailsStore.items;
  }

  @computed get workoutListIsEmpty() {
    return this.workouts.length === 0;
  }

  @computed get isFiltersEmpty() {
    const { filters } = CategoryDetailsStore;

    return areStatusActiveProgramFiltersEmpty(filters);
  }

  componentDidMount() {
    if (this.categoryId) {
      CategoryDetailsStore.applyFilter({
        column: "WorkoutCategoryId",
        value: this.categoryId,
      });
      Promise.all([
        InAppStudioStore.fetchCategoryDetails(this.categoryId),
        CategoryDetailsStore.fetch(),
        WorkoutTypeDictionary.fetch(),
        ActiveProgramDictionary.fetch(),
      ]);
    }
  }

  componentWillUnmount() {
    CategoryDetailsStore.reset();
  }

  isSpecialProgram = () => this.category?.family?.name === "Special Programs";

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  onBackClick = () => {
    this.props.history.push(ROUTES.IN_APP_STUDIO);
  };

  fetchMoreWorkouts = async () => {
    const { setMessage } = this.props;

    try {
      await CategoryDetailsStore.fetchMoreWorkouts();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    CategoryDetailsStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH && CategoryDetailsStore.fetch();
  };

  onSearchClear = () => {
    CategoryDetailsStore.clearSearch();
    CategoryDetailsStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      CategoryDetailsStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    CategoryDetailsStore.fetch();
  };

  onFilterClear = () => {
    CategoryDetailsStore.clearFilter(FILTER_COLUMNS.ACTIVE_PROGRAMS);
    CategoryDetailsStore.clearFilter(FILTER_COLUMNS.PUBLISH_STATUS);
    CategoryDetailsStore.fetch();
  };

  onAddWorkout = (dayWorkouts?: {
    day: Date;
    dayNumber: number;
    workouts: any[];
  }) => () => {
    const { openPopup } = this.props;

    const existingDayWorkoutsLabels = dayWorkouts?.workouts.map(
      ({ label }: { label: string }) => label,
    );

    openPopup({
      key: "AddWorkout",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      confirmation: {
        renderConfirmation: ({ deny, allow }) => (
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={deny}
            onAccept={allow}
          />
        ),
        shouldShowConfirmation: this.shouldShowConfirmation,
      },
      render: ({ close }) => (
        <WorkoutForm
          isLoading={false}
          onSubmit={this.onAddWorkoutSubmit(close, dayWorkouts?.dayNumber)}
          onClose={close}
          onChangeIsDirty={this.onChangeIsPopupDirty}
          categoryId={this.categoryId}
          isWeekCategory={this.isWeekCategory}
          existingDayWorkoutsLabels={existingDayWorkoutsLabels}
          shouldShowUpdateConfirmation={this.shouldShowUpdateConfirmation}
        />
      ),
    });
  };

  onAddWorkoutSubmit = (close: () => void, day?: number) => async (
    values: any,
  ) => {
    const { setMessage } = this.props;
    const {
      workouts,
      customLabel,
      workoutLabel,
      updateDateTime,
      ...restValues
    } = values;

    const payload = this.isWeekCategory
      ? {
          dayOfWeek: day ?? 0,
          workouts: Object.keys(restValues).map((key: string) => {
            const workoutType = WorkoutTypeDictionary.options.find(
              ({ label }) => label === key,
            );

            return {
              workoutId: restValues[key]?.id,
              workoutTypeId: workoutType?.value,
              label:
                workoutType?.label === "Custom"
                  ? customLabel.trim()
                  : undefined,
            };
          }),
          categoryId: this.categoryId,
        }
      : {
          workouts: values.workouts?.map(({ id }: { id: number }) => ({
            workoutId: id,
          })),
          categoryId: this.categoryId,
        };

    if (!payload.workouts || payload.workouts?.length === 0) {
      this.resetIsPopupDirty();
      close();
      return;
    }

    try {
      await CategoryDetailsStore.addWorkoutToCategory(payload);
      this.resetIsPopupDirty();
      close();
      CategoryDetailsStore.resetPage();
      CategoryDetailsStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onManageAccessPrograms = (workout: any) => () => {
    const { openPopup } = this.props;

    const shouldShowUpdateConfirmation =
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(workout.publishStatus) && this.shouldShowUpdateConfirmation;

    openPopup({
      key: "ManageCategory",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      confirmation: {
        renderConfirmation: ({ deny, allow }) => (
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={deny}
            onAccept={allow}
          />
        ),
        shouldShowConfirmation: this.shouldShowConfirmation,
      },
      render: ({ close }) => (
        <AccessForm
          isLoading={CategoryDetailsStore.isDetailsManaging}
          isFormLoading={CategoryDetailsStore.isWorkoutLoading}
          initialValues={{ categoryId: this.categoryId, workoutId: workout.id }}
          onSubmit={this.onManageAccessProgramsSubmit(workout, close)}
          onClose={close}
          onChangeIsDirty={this.onChangeIsPopupDirty}
          shouldShowUpdateConfirmation={shouldShowUpdateConfirmation}
        />
      ),
    });
  };

  onManageAccessProgramsSubmit = (workout: any, close: () => void) => async (
    values: any,
  ) => {
    const { setMessage } = this.props;
    const { activePrograms } = values;

    const payload = {
      categoryId: this.categoryId,
      workoutId: workout.id,
      activePrograms: activePrograms.map(
        ({ id, switchState }: { id: number; switchState: boolean }) => ({
          activeProgramId: id,
          permission: switchState
            ? PROGRAM_PERMISSION_TYPES.PREVIEW
            : PROGRAM_PERMISSION_TYPES.FULL_ACCESS,
        }),
      ),
    };

    try {
      await CategoryDetailsStore.updateWorkoutAccessPrograms(payload);
      this.resetIsPopupDirty();
      close();
      CategoryDetailsStore.resetPage();
      CategoryDetailsStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onRemoveWorkout = (workout: any, dayNumber?: number) => () => {
    const { openPopup } = this.props;

    const text = getConfirmRemovalText({
      entity: workout,
      suffix: "workout",
      category: this.category,
    });

    openPopup({
      key: "RemoveWorkout",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title="Remove Workout"
          text={text}
          declineText="Cancel"
          acceptText="Remove"
          onDecline={close}
          onAccept={this.onRemoveWorkoutSubmit(close, workout, dayNumber)}
          isLoading={CategoryDetailsStore.isDetailsManaging}
        />
      ),
    });
  };

  onRemoveWorkoutSubmit = (
    close: () => void,
    workout: any,
    day?: number,
  ) => async () => {
    const { setMessage } = this.props;

    const payload = this.isWeekCategory
      ? {
          dayOfWeek: day ?? 0,
          workoutIds: [workout?.id],
          categoryId: this.categoryId,
          label: workout.label || workout.title,
        }
      : { workoutIds: [workout?.id], categoryId: this.categoryId };

    try {
      await CategoryDetailsStore.removeWorkoutFromCategory(payload);
      close();
      CategoryDetailsStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onDetailsClick = (workout: any) => () => {
    const path = generatePath(
      ROUTES.IN_APP_STUDIO_CATEGORY_DETAILS_WORKOUT_DETAILS,
      { id: this.categoryId, workoutId: workout.id },
    );
    this.props.history.push({ pathname: path, state: { readOnly: true } });
  };

  onDragEnd = (dayOfWeek?: number) => async (result: DropResult) => {
    const { setMessage } = this.props;

    // dropped outside the list
    if (
      !result.destination ||
      !result.source ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    try {
      this.categoryId &&
        (await CategoryDetailsStore.reorderWorkouts({
          categoryId: this.categoryId,
          startIndex,
          endIndex,
          dayOfWeek,
        }));
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  shouldShowConfirmation = () => {
    return this.isPopupDirty;
  };

  render() {
    return (
      <Container header={<Header controls={<PublishButton />} />}>
        <Page
          topControls={
            <BackButtonWrapper>
              <Button
                color="secondary"
                variant="text"
                startIcon={<KeyboardBackspaceIcon />}
                onClick={this.onBackClick}
              >
                Back to categories
              </Button>
            </BackButtonWrapper>
          }
          title={this.categoryTitle}
          leftControls={
            this.categoryPublishStatus && (
              <PublishStatusWrapper>
                <PublishStatus status={this.categoryPublishStatus} />
              </PublishStatusWrapper>
            )
          }
          controls={
            !this.isWeekCategory && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={this.onAddWorkout()}
              >
                Add Workout
              </Button>
            )
          }
          isLoading={
            InAppStudioStore.isCategoryLoading || CategoryDetailsStore.isLoading
          }
        >
          {this.isWeekCategory ? (
            <>
              <WeekCategoryPageSubtitle>
                <Typography variant="h2" color="textPrimary">
                  {this.weekCategoryDays &&
                    getIntervalText(this.weekCategoryDays)}
                </Typography>
              </WeekCategoryPageSubtitle>
              {this.weekCategoryWorkouts?.map(
                (dayWorkouts: {
                  day: Date;
                  dayNumber: number;
                  workouts: any[];
                }) => (
                  <WeekCategoryContainer key={dayWorkouts.day.toString()}>
                    <WeekCategoryHeader>
                      <Wrapper>
                        <Typography variant="h3" color="textPrimary">
                          {getDayOfWeek(dayWorkouts.day)}
                        </Typography>
                        <Typography variant="subtitle1" color="secondary">
                          {getMonthWithDay(dayWorkouts.day)}
                        </Typography>
                      </Wrapper>
                      <Wrapper>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={this.onAddWorkout(dayWorkouts)}
                        >
                          Add Workout
                        </Button>
                      </Wrapper>
                    </WeekCategoryHeader>
                    <Divider />
                    <DragDropContext
                      onDragEnd={this.onDragEnd(dayWorkouts.dayNumber)}
                    >
                      <Droppable droppableId="droppable">
                        {(provided, snapshot) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            {dayWorkouts.workouts.length ? (
                              dayWorkouts.workouts.map((workout, index) => (
                                <Draggable
                                  key={workout.id}
                                  draggableId={workout.id.toString()}
                                  index={index}
                                  isDragDisabled={!this.isFiltersEmpty}
                                >
                                  {(provided, snapshot) => (
                                    <CategoryCardWrapper
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                    >
                                      <CategoryDetailsCard
                                        title={workout.label}
                                        duration={workout.durationText}
                                        image={workout.imageUrl}
                                        publishStatus={workout.publishStatus}
                                        onDetailsClick={this.onDetailsClick(
                                          workout,
                                        )}
                                        onRemove={this.onRemoveWorkout(
                                          workout,
                                          dayWorkouts.dayNumber,
                                        )}
                                        isDragging={snapshot.isDragging}
                                        dragHandleProps={
                                          provided.dragHandleProps ?? undefined
                                        }
                                      />
                                    </CategoryCardWrapper>
                                  )}
                                </Draggable>
                              ))
                            ) : (
                              <Placeholder
                                height={132}
                                width={132}
                                text={"No workouts added yet."}
                              />
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </WeekCategoryContainer>
                ),
              )}
            </>
          ) : (
            <>
              <SearchContainer>
                <Search
                  initValue={CategoryDetailsStore.search}
                  onApply={this.onSearchApply}
                  onClear={this.onSearchClear}
                />
                <SearchControls>
                  <SearchControlWrapper>
                    <FilteringControl
                      initValue={CategoryDetailsStore.filters}
                      onApply={this.onFilterApply}
                      onClear={this.onFilterClear}
                      statusFiltering
                      accessProgramsFiltering
                    />
                  </SearchControlWrapper>
                </SearchControls>
              </SearchContainer>

              {!this.workoutListIsEmpty ? (
                <>
                  {CategoryDetailsStore.isWorkoutReordering && (
                    <OverflowLoader />
                  )}
                  <InfiniteScroll
                    dataLength={this.workouts.length}
                    hasMore={CategoryDetailsStore.hasMoreInfo}
                    next={this.fetchMoreWorkouts}
                    height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
                    loader={<ScrollLoader />}
                  >
                    <DragDropContext onDragEnd={this.onDragEnd}>
                      <Droppable droppableId="droppable">
                        {(provided, snapshot) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            {this.workouts.map((workout, index) => (
                              <Draggable
                                key={workout.id}
                                draggableId={workout.id.toString()}
                                index={index}
                                isDragDisabled={!this.isFiltersEmpty}
                              >
                                {(provided, snapshot) => (
                                  <CategoryCardWrapper
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                  >
                                    <CategoryDetailsCard
                                      title={workout.title}
                                      duration={workout.durationText}
                                      image={workout.imageUrl}
                                      publishStatus={workout.publishStatus}
                                      onDetailsClick={this.onDetailsClick(
                                        workout,
                                      )}
                                      onAccessClick={this.onManageAccessPrograms(
                                        workout,
                                      )}
                                      onRemove={this.onRemoveWorkout(workout)}
                                      isDragging={snapshot.isDragging}
                                      showAccess={!this.isSpecialProgram()}
                                      dragHandleProps={provided.dragHandleProps}
                                    />
                                  </CategoryCardWrapper>
                                )}
                              </Draggable>
                            ))}

                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </InfiniteScroll>
                </>
              ) : (
                <Placeholder
                  height={280}
                  width={280}
                  text={
                    CategoryDetailsStore.search
                      ? "No matches found. Please try another search."
                      : "No workouts added yet."
                  }
                />
              )}
            </>
          )}
        </Page>
      </Container>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(CategoryDetails)));
